import constate from 'constate';
import { useCallback, useEffect } from 'react';
import { z } from 'zod';

import { BrowserStorageVariant, useBrowserStorage } from 'common';

const BROWSER_NOTIFICATIONS_SETTINGS_STORAGE_KEY =
  'staff.browserNotifications.notificationSettings' as const;

const BROWSER_NOTIFICATIONS_WATERMARK_STORAGE_KEY =
  'staff.browserNotifications.watermarkMs' as const;

const BROWSER_NOTIFICATIONS_CHANGE_EVENT_NAME = 'staff.browserNotifications.changed.v1' as const;

export enum OptInStatus {
  OptedIn = 'OptedIn',
  OptedOut = 'OptedOut',
  NotAsked = 'NotAsked',
}

function dispatchBrowserNotificationsChangedEvent() {
  try {
    window.dispatchEvent(new Event(BROWSER_NOTIFICATIONS_CHANGE_EVENT_NAME));
  } catch {
    // no-op
  }
}

function useBrowserNotifications() {
  const {
    storedValues: notificationSettings,
    setStoredValues: setNotificationSettings,
    refetchStoredValues: refetchNotificationSettings,
  } = useBrowserStorage({
    storageKeyName: BROWSER_NOTIFICATIONS_SETTINGS_STORAGE_KEY,
    variant: BrowserStorageVariant.LocalStorage,
    schema: z.object({ optInStatus: z.nativeEnum(OptInStatus) }),
    initialValue: { optInStatus: OptInStatus.NotAsked },
  });

  const { storedValues: watermarkSettings, setStoredValues: setWatermarkSettings } =
    useBrowserStorage({
      storageKeyName: BROWSER_NOTIFICATIONS_WATERMARK_STORAGE_KEY,
      variant: BrowserStorageVariant.LocalStorage,
      schema: z.object({ watermarkMilliseconds: z.number() }),
      initialValue: { watermarkMilliseconds: 0 },
    });

  const memoizedSetWatermarkMilliseconds = useCallback(
    (watermarkMilliseconds: number) => {
      setWatermarkSettings({ watermarkMilliseconds });
    },
    [setWatermarkSettings]
  );

  const isBrowserNotificationSupported = typeof window !== 'undefined' && 'Notification' in window;
  const browserNotificationPermission = isBrowserNotificationSupported
    ? Notification.permission
    : 'default';

  const optInToNotificationsInAppStatus = notificationSettings?.optInStatus ?? OptInStatus.NotAsked;
  const isBrowserNotificationEnabled = browserNotificationPermission === 'granted';
  const canRequestPermission =
    isBrowserNotificationSupported && browserNotificationPermission !== 'denied';

  const canEmitNotifications =
    isBrowserNotificationEnabled && optInToNotificationsInAppStatus === OptInStatus.OptedIn;

  const memoizedSetOptInStatusAndDispatchEvent = useCallback(
    (optInStatus: OptInStatus) => {
      setNotificationSettings({ optInStatus });
      dispatchBrowserNotificationsChangedEvent();
    },
    [setNotificationSettings]
  );

  const memoizedOnStorageEvent = useCallback(
    (event: StorageEvent) => {
      if (event.key !== BROWSER_NOTIFICATIONS_SETTINGS_STORAGE_KEY) {
        return;
      }
      refetchNotificationSettings();
    },
    [refetchNotificationSettings]
  );

  useEffect(() => {
    window.addEventListener(BROWSER_NOTIFICATIONS_CHANGE_EVENT_NAME, refetchNotificationSettings);

    return () => {
      window.removeEventListener(
        BROWSER_NOTIFICATIONS_CHANGE_EVENT_NAME,
        refetchNotificationSettings
      );
    };
  }, [refetchNotificationSettings, memoizedOnStorageEvent]);

  useEffect(() => {
    // Cross-tab updates (won't fire in the same tab that writes).
    window.addEventListener('storage', memoizedOnStorageEvent);
    return () => {
      window.removeEventListener('storage', memoizedOnStorageEvent);
    };
  }, [refetchNotificationSettings, memoizedOnStorageEvent]);

  async function requestBrowserNotificationPermissionAndOptIn(): Promise<NotificationPermission> {
    if (!isBrowserNotificationSupported) {
      memoizedSetOptInStatusAndDispatchEvent(OptInStatus.OptedOut);
      return 'default';
    }

    try {
      if (Notification.permission === 'granted') {
        memoizedSetOptInStatusAndDispatchEvent(OptInStatus.OptedIn);
        setWatermarkSettings({ watermarkMilliseconds: Date.now() });
        return 'granted';
      }

      const nextPermission = await Notification.requestPermission();
      if (nextPermission === 'granted') {
        memoizedSetOptInStatusAndDispatchEvent(OptInStatus.OptedIn);
        setWatermarkSettings({ watermarkMilliseconds: Date.now() });
      } else {
        memoizedSetOptInStatusAndDispatchEvent(OptInStatus.OptedOut);
      }

      return nextPermission;
    } catch {
      memoizedSetOptInStatusAndDispatchEvent(OptInStatus.OptedOut);
      return isBrowserNotificationSupported ? Notification.permission : 'default';
    }
  }

  return {
    isBrowserNotificationSupported,
    browserNotificationPermission,
    optInToNotificationsInAppStatus,
    isBrowserNotificationEnabled,
    canRequestPermission,
    canEmitNotifications,
    watermarkMilliseconds: watermarkSettings.watermarkMilliseconds,
    memoizedSetWatermarkMilliseconds,
    requestBrowserNotificationPermissionAndOptIn,
    disableBrowserNotifications: () => memoizedSetOptInStatusAndDispatchEvent(OptInStatus.OptedOut),
  };
}

export const [BrowserNotificationsProvider, useBrowserNotificationsContext] =
  constate(useBrowserNotifications);
